import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Save, X, Upload, ArrowUp, ArrowDown,
  Eye, EyeOff, Image as ImageIcon, Loader2, Layers
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  'Smartphone', 'Shirt', 'Sofa', 'Home', 'Car', 'BookOpen',
  'Dumbbell', 'UtensilsCrossed', 'Leaf', 'Ticket', 'Wrench', 'Sparkles',
  'Package', 'Gift', 'Music', 'Gamepad2', 'Camera', 'Watch'
];

const GRADIENT_PRESETS = [
  '#3B82F6, #06B6D4',
  '#EC4899, #F43F5E',
  '#F59E0B, #EA580C',
  '#10B981, #14B8A6',
  '#EF4444, #F97316',
  '#8B5CF6, #A855F7',
  '#22C55E, #84CC16',
  '#6366F1, #3B82F6',
  '#F59E0B, #EAB308',
  '#64748B, #475569',
];

const emptyForm = {
  name: '',
  icon_name: 'Sparkles',
  image_url: '',
  gradient: '#64748B, #475569',
  enabled: true,
  display_order: 999,
};

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API_URL}/api/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(res.data || []);
    } catch (e) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setEditingId(cat.id);
    setFormData({
      name: cat.name,
      icon_name: cat.icon_name || 'Sparkles',
      image_url: cat.image_url || '',
      gradient: cat.gradient || '#64748B, #475569',
      enabled: cat.enabled !== false,
      display_order: cat.display_order || 999,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Invalid image type (jpg, png, gif, webp)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Max 10MB');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API_URL}/api/upload/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = `${API_URL}${res.data.url}`;
      setFormData((p) => ({ ...p, image_url: url }));
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveCategory = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const token = localStorage.getItem('admin_token');
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/categories/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/api/admin/categories`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Category created');
      }
      closeForm();
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggleEnabled = async (cat) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${API_URL}/api/admin/categories/${cat.id}`,
        { enabled: !cat.enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${cat.name} ${!cat.enabled ? 'enabled' : 'disabled'}`);
      fetchCategories();
    } catch (e) {
      toast.error('Failed to toggle category');
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Existing listings will keep this category label.`)) return;
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${cat.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Category deleted');
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= categories.length) return;
    const reordered = [...categories];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setCategories(reordered);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(
        `${API_URL}/api/admin/categories/reorder`,
        { ordered_ids: reordered.map((c) => c.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      toast.error('Reorder failed');
      fetchCategories();
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Layers className="w-7 h-7 text-indigo-400" />
            Categories
          </h1>
          <p className="text-slate-400">Add, edit, reorder, enable/disable product categories shown on the homepage.</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat, idx) => (
          <div
            key={cat.id}
            className={`bg-slate-800 rounded-2xl border ${cat.enabled ? 'border-slate-700' : 'border-red-500/30 opacity-70'} overflow-hidden`}
            data-testid={`category-card-${cat.name}`}
          >
            <div
              className="aspect-video relative"
              style={{ background: `linear-gradient(135deg, ${cat.gradient})` }}
            >
              {cat.image_url ? (
                <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/70">
                  <ImageIcon className="w-10 h-10" />
                </div>
              )}
              <div className="absolute top-2 right-2 flex gap-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="p-1.5 bg-black/50 rounded-md text-white hover:bg-black/70 disabled:opacity-40"
                  data-testid={`move-up-${cat.name}`}
                  title="Move up"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === categories.length - 1}
                  className="p-1.5 bg-black/50 rounded-md text-white hover:bg-black/70 disabled:opacity-40"
                  data-testid={`move-down-${cat.name}`}
                  title="Move down"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
              <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-md">#{cat.display_order}</span>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-white">{cat.name}</h3>
                  <p className="text-xs text-slate-500">{cat.icon_name}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${cat.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                  {cat.enabled ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openEdit(cat)}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg"
                  data-testid={`edit-category-${cat.name}`}
                >
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  onClick={() => toggleEnabled(cat)}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg"
                  data-testid={`toggle-category-${cat.name}`}
                >
                  {cat.enabled ? <><EyeOff className="w-3.5 h-3.5" /> Disable</> : <><Eye className="w-3.5 h-3.5" /> Enable</>}
                </button>
                <button
                  onClick={() => deleteCategory(cat)}
                  className="px-3 py-2 bg-red-600/80 hover:bg-red-600 text-white text-sm rounded-lg"
                  data-testid={`delete-category-${cat.name}`}
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeForm}>
          <div
            className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">
                {editingId ? 'Edit Category' : 'New Category'}
              </h2>
              <button onClick={closeForm} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. Tools, Music Gear"
                  data-testid="category-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Image</label>
                <div className="flex items-center gap-4">
                  <div
                    className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${formData.gradient})` }}
                  >
                    {formData.image_url ? (
                      <img src={formData.image_url} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/70">
                        <ImageIcon className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm disabled:opacity-60"
                      data-testid="upload-category-image-btn"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files?.[0])}
                    />
                    <input
                      type="text"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="Or paste image URL"
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Background Gradient</label>
                <div className="grid grid-cols-5 gap-2 mb-2">
                  {GRADIENT_PRESETS.map((g) => (
                    <button
                      key={g}
                      onClick={() => setFormData({ ...formData, gradient: g })}
                      className={`h-10 rounded-lg border-2 transition ${formData.gradient === g ? 'border-white scale-105' : 'border-transparent'}`}
                      style={{ background: `linear-gradient(135deg, ${g})` }}
                      title={g}
                    />
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.gradient}
                  onChange={(e) => setFormData({ ...formData, gradient: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono"
                  placeholder="#3B82F6, #06B6D4"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Icon</label>
                  <select
                    value={formData.icon_name}
                    onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white"
                  >
                    {ICON_OPTIONS.map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Display Order</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 999 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="w-5 h-5 rounded accent-indigo-600"
                />
                <span className="text-white text-sm">Enabled (visible on homepage)</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-slate-700">
              <Button variant="outline" onClick={closeForm} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                Cancel
              </Button>
              <Button onClick={saveCategory} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-category-btn">
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Save Changes' : 'Create Category'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
